import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";

import { X, RefreshCw } from "lucide-react";
import { Suspense, useEffect, useMemo, useRef, useState } from "react";

import clsx from "clsx";
import { getDynamicComponent } from "@/features/tabloader/get-dynamic-component";

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";

import {
  arrayMove,
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";

import { CSS } from "@dnd-kit/utilities";
import React from "react";
import { useTabStore } from "@/stores/use-tab-store";
import NotFound from "@/pages/NotFoundPage";
import { useI18n } from "@/contexts/I18nContext";

function SortableTabTrigger({ tab, onRemove, onCloseOthers }) {
  const { t } = useI18n();
  const localizedTitle = tab.menuId
    ? t(`menu.${tab.menuId}`, tab.title)
    : tab.title;
  const { setActiveTab, activeTab } = useTabStore();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tab.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 999 : undefined,
    cursor: isDragging ? "grabbing" : "grab",
  };

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <TabsTrigger
          ref={setNodeRef}
          value={tab.id}
          {...attributes}
          {...listeners}
          style={style}
          onClick={(e) => {
            e.stopPropagation();
            setActiveTab(tab.id);
          }}
          className={clsx(
            "relative flex-none items-center px-3 py-1.5",
            "h-9",
            "border border-zinc-800",
            "shadow transition-colors",
            "min-w-16",
            "max-w-[2000px]",
            activeTab === tab.id
              ? "bg-white text-black"
              : "bg-zinc-900 text-zinc-300 hover:bg-zinc-700 hover:text-white"
          )}
        >
          <div className="flex items-center">
            <span className="truncate">{localizedTitle}</span>
            <div
              onClick={(e) => {
                e.stopPropagation();
                onRemove(tab.id);
              }}
              className="ml-2 cursor-pointer p-0.5 text-zinc-400 transition-colors hover:text-red-500"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onRemove(tab.id);
                }
              }}
              aria-label={t("common.close")}
            >
              <X size={12} />
            </div>
          </div>
        </TabsTrigger>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem
          onClick={() => onRemove(tab.id)}
          className="text-red-600 focus:text-red-600"
        >
          {t("common.close")}
        </ContextMenuItem>
        <ContextMenuItem onClick={() => onCloseOthers(tab.id)}>
          {t("common.closeOthers")}
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}

export function TabView() {
  const { t } = useI18n();
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const { tabs, activeTab, setActiveTab, removeTab, removeOtherTabs } =
    useTabStore();

  const iframeRefs = useRef({});
  const componentsCache = useRef({});

  // 동기화된 탭 정렬 상태
  const [tabOrder, setTabOrder] = useState(tabs.map((t) => t.id));

  useEffect(() => {
    setTabOrder((prevOrder) => {
      const tabIds = tabs.map((t) => t.id);
      const added = tabIds.filter((id) => !prevOrder.includes(id));
      const removed = prevOrder.filter((id) => !tabIds.includes(id));
      if (added.length === 0 && removed.length === 0) return prevOrder;
      const cleaned = prevOrder.filter((id) => !removed.includes(id));
      return [...cleaned, ...added];
    });
  }, [tabs]);

  const sortedTabs = useMemo(() => {
    return tabOrder.map((id) => tabs.find((t) => t.id === id)).filter(Boolean);
  }, [tabOrder, tabs]);

  tabs.forEach((tab) => {
    if (
      tab.type === "component" &&
      tab.id &&
      !componentsCache.current[tab.id]
    ) {
      const comp = getDynamicComponent(tab.id);
      if (comp) {
        componentsCache.current[tab.id] = comp;
        comp.preload?.();
      }
    }
  });

  if (tabs.length === 0)
    return <div className="text-muted p-4">{t("common.noTabs")}</div>;

  const handleRefresh = () => {
    const tab = tabs.find((t) => t.id === activeTab);
    if (!tab) return;
    if (tab.type === "iframe" && iframeRefs.current[tab.id]) {
      iframeRefs.current[tab.id]?.contentWindow?.location.reload();
    } else if (tab.type === "component" && tab.id) {
      delete componentsCache.current[tab.id];
      setActiveTab("");
      setTimeout(() => setActiveTab(tab.id), 0);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = tabOrder.findIndex((id) => id === active.id);
    const newIndex = tabOrder.findIndex((id) => id === over.id);
    if (oldIndex !== -1 && newIndex !== -1) {
      const newOrder = arrayMove(tabOrder, oldIndex, newIndex);
      setTabOrder(newOrder);
    }
  };

  return (
    <Tabs
      value={activeTab || ""}
      onValueChange={setActiveTab}
      className="bg-background flex h-full min-h-0 min-w-0 w-full flex-col overflow-hidden rounded-none border shadow-sm"
    >
      <div className="bg-background relative flex flex-1 flex-col overflow-hidden p-0">
        {/* Header */}
        <div className="flex min-w-0 items-center rounded-none border-zinc-300 bg-zinc-50/80 px-4 py-2 dark:border-zinc-700 dark:bg-zinc-900/80">
          <span className="min-w-0 truncate text-lg font-semibold text-zinc-800 dark:text-zinc-100">
            {(() => {
              const tab = tabs.find((item) => item.id === activeTab);
              return tab?.menuId
                ? t(`menu.${tab.menuId}`, tab.title)
                : tab?.title || "";
            })()}
          </span>
          <div className="flex-1" />
          {activeTab && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => removeTab(activeTab)}
                className="rounded p-1 text-zinc-400 transition-colors hover:text-red-500"
                aria-label={t("common.close")}
                type="button"
              >
                <X size={16} />
              </button>
              <button
                onClick={handleRefresh}
                className="rounded p-1 text-zinc-400 transition-colors hover:text-blue-500"
                aria-label={t("common.refresh")}
                type="button"
              >
                <RefreshCw size={16} />
              </button>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="relative min-h-0 flex-1">
          {tabs.map((tab) => {
            if (tab.type === "iframe") {
              return (
                <iframe
                  key={tab.id}
                  ref={(el) => {
                    if (el) iframeRefs.current[tab.id] = el;
                  }}
                  src={tab.url}
                  title={tab.title}
                  className={clsx(
                    "absolute inset-0 h-full w-full border-none",
                    tab.id === activeTab ? "block" : "hidden"
                  )}
                />
              );
            }

            const Comp = tab.id ? componentsCache.current[tab.id] : null;
            return (
              <div
                key={tab.id}
                className={clsx(
                  "absolute inset-0 overflow-auto",
                  tab.id === activeTab ? "block" : "hidden"
                )}
              >
                <Suspense fallback={<div className="p-4">{t("common.loading")}</div>}>
                  {Comp ? <Comp /> : <NotFound />}
                </Suspense>
              </div>
            );
          })}
        </div>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={tabOrder}
          strategy={horizontalListSortingStrategy}
        >
          <TabsList
            className={clsx(
              "w-full",
              "flex h-10 justify-start gap-1 px-2",
              "border-t border-zinc-800 bg-black text-white shadow-md",
              "scrollbar-hide min-w-0 overflow-x-auto overflow-y-hidden rounded-none"
            )}
          >
            {sortedTabs.map((tab) => (
              <SortableTabTrigger
                key={tab.id}
                tab={tab}
                onRemove={removeTab}
                onCloseOthers={removeOtherTabs}
              />
            ))}
          </TabsList>
        </SortableContext>
      </DndContext>
    </Tabs>
  );
}
